// Janus C1 (2026-09-24): port of compute_attacks() (includes/functions/janus/core/compute/attacks.php) — the per-turn attack budget
// and its combat-bucket reconciliation. Until this ran, the possessor compile carried attacks.* / combat.extra_attack /
// combat.hindered_attack_counter from PRIOR, so an extra-attack weapon (a Greatsword's granted "Execution Rhythm") was refused.
//   per_turn = Base attack (1) + host + default-provider carriers + dynamic (status / passive / inventory / feat), inventory capped
//   combat.extra_attack = per_turn - 1; combat.hindered_attack_counter = weapon_attack - extra; attacks.extra_attack = the non-base rows
// A supplied finalized form replaces the authored base; dynamic modifiers still belong to the host.
import type { ItemFieldRow } from './item-field-reader';
import type { CreatureAttackFoldInput, CreatureAttackFoldResult, CreatureReachSource } from '../types';
import { readLayerScoringToken, readNodeItemId } from './layer-cell-readers';
import { createCatalogDefaultProviderReader } from './provider-catalog-order';
import type { ProviderCatalogRows } from './provider-catalog-order';
import { createStatusLayerReadContext } from './status-row-construction';
import { createFormulaReadContext } from './formula-read-context';
import { readCreaturePayloadStatuses } from './creature-status-reader';
import { createDynamicIntegerSourceCollector } from './dynamic-integer-sources';
import type { DynamicIntegerSourceDependencies } from './dynamic-integer-sources';
import { createComputeSourceCleaner } from './compute-source-cleanup';
import type { ComputeSourceCleanupDependencies } from './compute-source-cleanup';
import { capInventorySourceRows, createComputedPersistence } from './computed-persistence';
import { scoreCarrierIntegerLayer } from './formula-variable-resolution';
import { integerCompileWorkingRow, putIntegerCompilePath } from './integer-compute-slots';

type Row = Record<string, unknown>;
const arrayLike = (value: unknown): value is ItemFieldRow => value !== null && typeof value === 'object';
const own = (value: unknown, key: string): unknown => arrayLike(value) && Object.prototype.hasOwnProperty.call(value, key)
    ? (value as Row)[key] : undefined;
const clone = <T>(value: T): T => value === undefined ? value : JSON.parse(JSON.stringify(value)) as T;
const scalar = (value: unknown): unknown => arrayLike(value) && Object.prototype.hasOwnProperty.call(value, '0') ? own(value, '0') : value;
const stringValue = (value: unknown): string => typeof scalar(value) === 'string' ? String(scalar(value)) : '';
const intValue = (value: unknown): number => { const n = Number(scalar(value) ?? 0); return Number.isFinite(n) ? Math.trunc(n) : 0; };
const LAYER = 'extra_attack';
const BASE = 1; // FT_COMPUTE_ATTACKS_BASE

export interface CreatureAttacksDependencies extends DynamicIntegerSourceDependencies, ComputeSourceCleanupDependencies {
    catalogRows: ProviderCatalogRows;
}
/** The recompiled sections: computed.creature.attacks (whole) and computed.creature.combat (whole — only the two leaves move). */
export type CreatureAttacksCompileResult = { ok: true; attacks: Row | undefined; combat: Row | undefined } | { ok: false; reason: string };

/** ft_compute_collect_{host,carrier}_source read the pristine slot in isolation (the speeds port's twin). */
export function pristineFragments(row: ItemFieldRow, layer: string): unknown[] {
    const bucket = own(row, 'item_layer_types');
    if (!arrayLike(bucket) || !Object.prototype.hasOwnProperty.call(bucket, layer)) return [];
    const slot = own(bucket, layer);
    const values = arrayLike(slot) && Object.prototype.hasOwnProperty.call(slot, '0') ? Object.values(slot) : [slot];
    return values.filter(value => { const token = readLayerScoringToken(value); return token !== null && token.trim() !== ''; });
}
export function sourceRow(category: string, label: string, value: number, hrefId: string, kind: string): Row {
    return { category, label: label.trim() === '' ? 'Unknown source' : label, value, href_id: hrefId, kind };
}

export function compileCreatureAttacks(input: ItemFieldRow, deps: CreatureAttacksDependencies, form: ItemFieldRow | null = null): CreatureAttacksCompileResult {
    const host = integerCompileWorkingRow(input);
    const base = form ?? host;
    const readers = createStatusLayerReadContext(deps);
    const formulaReaders = createFormulaReadContext(deps);
    const providers = createCatalogDefaultProviderReader({ catalogRows: deps.catalogRows, nodeItemId: readNodeItemId, resolveCarrierRef: deps.resolveCarrierRef });
    const cleaner = createComputeSourceCleaner(deps);
    const persist = createComputedPersistence(deps, cleaner);
    const collector = createDynamicIntegerSourceCollector(deps);

    // ft_shapeshift_form_arr_for_speeds(): a held creature-typed status is a FORM whose authored extra_attack replaces the base.
    for (const status of Object.values(readCreaturePayloadStatuses(host, readers.layerValue, deps.resolveCarrierRef)).filter(arrayLike)) {
        const def = own(status, 'status_arr');
        if (!arrayLike(def)) return { ok: false, reason: 'unported:attacks:status-undefined' };
        if (formulaReaders.itemField(def, 'item_type') === 'creature' && !form) return { ok: false, reason: 'unported:attacks:shapeshift' };
    }
    if (!arrayLike(own(host, 'computed'))) putIntegerCompilePath(host, ['computed'], {});
    if (!arrayLike(own(own(host, 'computed'), 'creature'))) putIntegerCompilePath(host, ['computed', 'creature'], {});

    const score = (fragments: unknown[]): number => scoreCarrierIntegerLayer({ item_layer_types: { [LAYER]: fragments } } as ItemFieldRow, LAYER, 0, false, deps.metadata, formulaReaders);
    for (const row of [base, ...providers.providers(base)]) for (const fragment of pristineFragments(row, LAYER)) {
        if ((readLayerScoringToken(fragment) ?? '').includes('[')) return { ok: false, reason: 'unported:attacks:token-reference' };
    }
    // 0. the innate Base attack — a 'base' row, `host` icon
    let sources: Row[] = [sourceRow('base', 'Base attack', BASE, '', 'host')];
    // 1. HOST: the authored slot alone
    const direct = pristineFragments(base, LAYER);
    if (direct.length) {
        const value = score(direct);
        // PHP: `item_name ?? 'Host'` — only a MISSING name reads Host; an empty one becomes 'Unknown source' (sourceRow) (Codex review)
        const hostName = formulaReaders.itemField(base, 'item_name');
        if (value !== 0) sources.push(sourceRow('host', hostName == null ? 'Host' : stringValue(hostName), value, stringValue(formulaReaders.itemField(base, 'item_id')), 'host'));
    }
    // 2. CARRIERS: each default provider's own contribution
    for (const carrier of providers.providers(base)) {
        const fragments = pristineFragments(carrier, LAYER);
        if (!fragments.length) continue;
        const value = score(fragments);
        if (value === 0) continue;
        const items = own(carrier, 'items'), type = stringValue(own(items, 'item_type')) || 'carrier';
        sources.push(sourceRow(type, stringValue(own(items, 'item_name')), value, stringValue(own(items, 'item_id')), type));
    }
    // 3. STATUS / PASSIVE / INVENTORY / FEAT via the shared dynamic walker (own layer only, feats included)
    let dynamicRows: Row[];
    try { [dynamicRows] = collector.dynamic(host, LAYER, true, true, form); }
    catch (error) { return { ok: false, reason: 'unported:attacks:walker:' + (error instanceof Error ? error.message : String(error)).slice(0, 60) }; }
    sources.push(...dynamicRows);
    // 3b. inventory non-stacking: only the single highest equipped item counts
    [sources] = capInventorySourceRows(sources as unknown as ItemFieldRow) as unknown as [Row[], number];
    sources = Object.values(sources);
    // 4. TOTAL = the row sum
    const total = sources.reduce((sum, row) => sum + intValue(row.value ?? 0), 0);
    // 5. per_turn (never elides: total ≥ 1)
    let out = persist(host, 'attacks', 'per_turn', total, { base: 0, sources, cap: null, locked: false });
    // 6. combat reconciliation from the authoritative budget
    const extra = total - BASE;
    const weaponAttack = intValue(own(own(host, 'encounter_creatures'), 'weapon_attack') ?? 0);
    out = persist(out, 'combat', 'extra_attack', extra);
    out = persist(out, 'combat', 'hindered_attack_counter', weaponAttack - extra);
    // 7. the extra-attack breakdown (non-base rows); a net-zero extra passes NO sources so the bucket elides
    const extraSources = sources.filter(row => { const cat = stringValue(row.category ?? row.kind ?? ''); return cat !== 'base' && cat !== 'remainder'; });
    out = persist(out, 'attacks', 'extra_attack', extra, { base: 0, sources: extra !== 0 ? extraSources : [] });
    const creature = own(own(out, 'computed'), 'creature');
    return { ok: true, attacks: clone(own(creature, 'attacks') as Row | undefined), combat: clone(own(creature, 'combat') as Row | undefined) };
}

// Restored 2026-09-27 (L3 typecheck fix): the pure ledger fold the 2026-09-24 rewrite dropped while `janus-engine/index.ts`
// kept re-exporting it and tools/test/janus-creature-attacks-php-ts-parity.mjs + janus-creature-attacks-dummy-staging-smoke.mjs
// still call it (the janus-engine typecheck failed on the dangling export). Verbatim from abcd199267.
/** Fold an already-resolved attack source ledger into its combat and display totals. */
export function foldCreatureAttackSources(input: CreatureAttackFoldInput): CreatureAttackFoldResult {
    const sources: CreatureReachSource[] = input.sources.map(source => ({ ...source }));
    const inventory = sources.map((source, index) => source.kind === 'inventory_equipped' ? index : -1).filter(index => index >= 0);
    if (inventory.length > 1) {
        let winner = inventory[0] ?? 0;
        for (const index of inventory.slice(1)) if ((sources[index]?.value ?? 0) > (sources[winner]?.value ?? 0)) winner = index;
        for (const index of inventory) {
            if (index === winner) continue;
            const source = sources[index];
            if (source) sources[index] = { ...source, value: 0, raw_value: source.value, suppressed: true };
        }
    }
    const total = sources.reduce((sum, source) => sum + source.value, 0);
    const authoritativeExtra = total - 1;
    const extraSources = sources.filter(source => {
        const category = typeof source.category === 'string' ? source.category : source.kind;
        return category !== 'base' && category !== 'remainder';
    });
    const combatExtraAttack = authoritativeExtra === 0 ? null : authoritativeExtra;
    const hindered = input.weaponAttack - authoritativeExtra;
    return {
        perTurn: { total, sources },
        combatExtraAttack,
        hinderedAttackCounter: hindered === 0 ? null : hindered,
        extraAttack: authoritativeExtra === 0 ? null : { total: authoritativeExtra, sources: extraSources },
    };
}
